import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, LogIn, Mail, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useGoogleLogin } from "@react-oauth/google";
import {
  accentText,
  cardClass,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from "../components/register/styles";
import { ApiError, authService } from "../auth/auth";
import { Field } from "../components/register/components/Field";
import { useAuthStore } from "../auth/auth.store";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const login = useAuthStore((state) => state.login);
  const authenticateWithGoogle = useAuthStore((state) => state.googleAuth);

  const googleEnabled = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ email: string; password: string }>();

  const onSubmit = async (values: {
  email: string;
  password: string;
}) => {
  setLoading(true);
  setApiError("");

  try {
    const success = await login(
      values.email,
      values.password
    );

    if (success) {
      navigate("/");
    }
  } catch (err) {
    if (err instanceof ApiError) {
      setApiError(err.message);
    } else {
      setApiError(t("apiNetworkError"));
    }
  } finally {
    setLoading(false);
  }
};

  const googleLogin = useGoogleLogin({
    scope: "openid email profile",
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setApiError("");
      try {
        await authenticateWithGoogle(tokenResponse.access_token);
        navigate("/success");
      } catch (err) {
        if (err instanceof ApiError) setApiError(err.message);
        else setApiError(t("apiNetworkError"));
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => {
      setApiError(t("apiNetworkError"));
    },
  });

  const handleForgotPassword = async () => {
    if (!forgotEmail) return;
    setForgotLoading(true);
    try {
      await authService.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch {
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      {/* ─── Hero ─── */}
      <section
        className="relative hidden lg:flex lg:flex-col lg:justify-between lg:overflow-hidden lg:p-14"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0,5,16,0.85), rgba(12,55,42,0.5), rgba(0,5,16,0.75)), url(https://images.pexels.com/photos/7693729/pexels-photo-7693729.jpeg?auto=compress&cs=tinysrgb&fit=crop&h=1400&w=2200)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >

        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.1 }} className="max-w-lg mt-30 text-white">
          <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight">{t("loginTitle")}</h1>
          <p className="mt-5 text-lg leading-relaxed text-white/80">{t("loginSubtitle")}</p>
          <div className="mt-8 flex flex-col gap-3">
            {[
              { icon: "🔒", text: t("featureEncryption") },
              { icon: "🛡️", text: t("featureCompliance") },
              { icon: "⚡", text: t("featureFast") },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                className="flex items-center gap-3 text-sm text-white/70"
              >
                <span className="text-lg">{item.icon}</span>
                <span>{item.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="flex gap-6 text-xs text-white/50">
          <span>256-bit SSL</span><span>•</span><span>GDPR Compliant</span><span>•</span><span>SOC 2</span>
        </div>
      </section>

      {/* ─── Form ─── */}
      <section className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-[var(--color-grey)] via-[var(--color-white)] to-[var(--color-grey)] p-4 dark:from-[var(--color-darkmode)] dark:via-[var(--color-deepSlate)] dark:to-[var(--color-darkmode)] sm:p-10">

        <div className="mt-30 w-1/1">

        <motion.div aria-hidden initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="pointer-events-none absolute -end-32 -top-20 h-96 w-96 rounded-full bg-[var(--color-tealGreen)]/10 blur-[100px] dark:bg-[var(--color-primary)]/8" />
        <motion.div aria-hidden initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="pointer-events-none absolute -start-28 bottom-0 h-80 w-80 rounded-full bg-[var(--color-secondary)]/10 blur-[100px] dark:bg-[var(--color-primary)]/6" />

        <div className={cardClass}>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.6 }} className="mb-6 h-1 w-full origin-left rounded-full bg-gradient-to-r from-[var(--color-tealGreen)] via-[var(--color-secondary)] to-[var(--color-tealGreen)] dark:from-[var(--color-primary)] dark:via-[var(--color-secondary)] dark:to-[var(--color-primary)]" />

          <div className="mb-6 flex items-start justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--color-tealGreen)] dark:text-[var(--color-primary)]">{t("brand")}</p>
              <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[var(--color-midnight_text)] dark:text-[var(--color-white)]">{t("loginTitle")}</h2>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--color-tealGreen)]/10 dark:bg-[var(--color-primary)]/15">
              <LogIn size={20} className={accentText} />
            </div>
          </div>

          {/* ── Google login ── */}
          <div className="mb-6">
              <button
                type="button"
                onClick={() => googleLogin()}
                disabled={googleLoading || !googleEnabled}
                className="inline-flex w-full items-center justify-center gap-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-white)] px-5 py-3.5 text-sm font-semibold text-[var(--color-midnight_text)] shadow-sm transition-all duration-200 hover:border-[var(--color-tealGreen)]/40 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:pointer-events-none dark:border-[var(--color-dark_border)] dark:bg-[var(--color-slateGray)] dark:text-[var(--color-white)] dark:hover:border-[var(--color-primary)]/40"
              >
                {googleLoading ? (
                  <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
                    <path fill="#4285F4" d="M21.8 12.2c0-.8-.1-1.6-.2-2.3H12v4.4h5.5a4.7 4.7 0 0 1-2 3.1v2.6h3.2c1.9-1.8 3.1-4.5 3.1-7.8Z" />
                    <path fill="#34A853" d="M12 22c2.7 0 5-.9 6.7-2.5l-3.2-2.6c-.9.6-2.1 1-3.5 1-2.7 0-5-1.8-5.8-4.3H2.9v2.7A10 10 0 0 0 12 22Z" />
                    <path fill="#FBBC05" d="M6.2 13.6A6 6 0 0 1 5.9 12c0-.6.1-1.1.3-1.6V7.7H2.9A10 10 0 0 0 2 12c0 1.6.4 3.2 1 4.3l3.2-2.7Z" />
                    <path fill="#EA4335" d="M12 6.1c1.5 0 2.8.5 3.9 1.5l2.9-2.9A9.8 9.8 0 0 0 12 2 10 10 0 0 0 2.9 7.7l3.3 2.7c.8-2.5 3.1-4.3 5.8-4.3Z" />
                  </svg>
                )}
                {googleLoading ? t("submitting") : t("googleLogin")}
              </button>
              {!googleEnabled && <p className="mt-2 text-xs text-[var(--color-warning)]">{t("googleSetupHint")}</p>}

              <div className="my-5 flex items-center gap-4">
                <div className="h-px flex-1 bg-[var(--color-border)] dark:bg-[var(--color-dark_border)]" />
                <span className="text-xs font-medium text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">{t("orContinueWith")}</span>
                <div className="h-px flex-1 bg-[var(--color-border)] dark:bg-[var(--color-dark_border)]" />
              </div>
            </div>

          {/* ── Email / Password form ── */}
          <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
            <Field label={t("email")} error={errors.email?.message} icon={<Mail size={14} className={accentText} />}>
              <input required type="email" className={inputClass} placeholder="name@example.com" {...register("email")} />
            </Field>
            <Field label={t("password")} error={errors.password?.message} icon={<KeyRound size={14} className={accentText} />}>
              <input required type="password" className={inputClass} placeholder="••••••••" {...register("password")} />
            </Field>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                className="text-xs font-semibold text-[var(--color-tealGreen)] transition hover:text-[var(--color-secondary)] dark:text-[var(--color-primary)] dark:hover:text-[var(--color-secondary)]"
              >
                {t("forgotPassword")}
              </button>
            </div>

            {apiError && (
              <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2.5 text-center text-sm font-medium text-[var(--color-error)]">
                {apiError}
              </motion.div>
            )}

            <button type="submit" disabled={loading} className={`${primaryButtonClass} w-full`}>
              {loading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : t("login")}
            </button>
          </form>

          {/* ── Register link ── */}
          <div className="mt-7 flex items-center justify-center gap-2 text-sm text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">
            <UserPlus size={15} className={accentText} />
            <span>{t("noAccount")}</span>
            <Link to="/register" className="font-semibold text-[var(--color-tealGreen)] transition hover:text-[var(--color-secondary)] dark:text-[var(--color-primary)] dark:hover:text-[var(--color-secondary)]">
              {t("goToRegister")}
            </Link>
          </div>
        </div>
        </div>

      </section>

      {/* ── Forgot password modal ── */}
      <AnimatePresence>
        {showForgot && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
            onClick={() => { setShowForgot(false); setForgotSent(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className={`max-w-sm ${cardClass}`}
            >
              <h3 className="text-lg font-bold text-[var(--color-midnight_text)] dark:text-[var(--color-white)]">{t("forgotPasswordTitle")}</h3>
              <p className="mt-2 text-sm text-[var(--color-charcoalGray)] dark:text-[var(--color-muted)]">{t("forgotPasswordDescription")}</p>

              {!forgotSent ? (
                <div className="mt-5 space-y-4">
                  <Field label={t("email")} icon={<Mail size={14} className={accentText} />}>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className={inputClass}
                      placeholder="name@example.com"
                    />
                  </Field>
                  <div className="flex gap-3">
                    <button onClick={handleForgotPassword} disabled={forgotLoading || !forgotEmail} className={primaryButtonClass}>
                      {forgotLoading ? <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : t("sendResetLink")}
                    </button>
                    <button onClick={() => setShowForgot(false)} className={secondaryButtonClass}>{t("back")}</button>
                  </div>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5">
                  <p className="text-sm font-medium text-[var(--color-success)]">{t("resetLinkSent")}</p>
                  <button onClick={() => { setShowForgot(false); setForgotSent(false); }} className={`${secondaryButtonClass} mt-4`}>{t("back")}</button>
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
